import {
	modulesList,
	moduleByID,
	moduleCreate,
	moduleUpdate,
	moduleDestroy,
	modulePlatformOverview,
	companyModuleAccess,
	toggleCompanyModule,
	bulkToggleCompanyModules,
	userModuleAccess,
	toggleUserModule,
	removeUserModuleAccess,
	companiesList,
} from '@/services/modules.service'

export default {
	namespaced: true,
	state: {
		// MODULES
		modulesList: {
			meta: {
				pagination: {
					total: 0,
				},
			},
		},
		module: {},
		modulesStatus: 'pending',
		modulesSaveStatus: 'init',
		modulesDeleteStatus: 'init',

		// PLATFORM OVERVIEW
		platformOverview: [],
		platformOverviewStatus: 'init',

		// COMPANY MODULE ACCESS
		companyModules: [],
		companyModulesStatus: 'init',
		companyToggleStatus: 'init',

		// USER MODULE ACCESS
		userModules: { data: [] },
		userModulesStatus: 'init',
		userToggleStatus: 'init',
	},
	getters: {
		// MODULES
		getModulesList: state => state.modulesList,
		getModulesStatus: state => state.modulesStatus,
		getModulesSaveStatus: state => state.modulesSaveStatus,
		getModulesDeleteStatus: state => state.modulesDeleteStatus,
		getModule: state => state.module,

		// PLATFORM OVERVIEW
		getPlatformOverview: state => state.platformOverview,
		getPlatformOverviewStatus: state => state.platformOverviewStatus,

		// COMPANY
		getCompanyModules: state => state.companyModules,
		getCompanyModulesStatus: state => state.companyModulesStatus,
		getCompanyToggleStatus: state => state.companyToggleStatus,

		// USER
		getUserModules: state => state.userModules,
		getUserModulesStatus: state => state.userModulesStatus,
		getUserToggleStatus: state => state.userToggleStatus,
	},
	mutations: {
		// MODULES
		SET_MODULES_LIST(state, data) {
			state.modulesList = data
		},
		SET_MODULE(state, data) {
			state.module = data
		},
		SET_MODULES_STATUS(state, status) {
			state.modulesStatus = status
		},
		SET_MODULES_SAVE_STATUS(state, status) {
			state.modulesSaveStatus = status
		},
		SET_MODULES_DELETE_STATUS(state, status) {
			state.modulesDeleteStatus = status
		},

		// PLATFORM OVERVIEW
		SET_PLATFORM_OVERVIEW(state, data) {
			state.platformOverview = data
		},
		SET_PLATFORM_OVERVIEW_STATUS(state, status) {
			state.platformOverviewStatus = status
		},

		// COMPANY
		SET_COMPANY_MODULES(state, data) {
			state.companyModules = data
		},
		SET_COMPANY_MODULES_STATUS(state, status) {
			state.companyModulesStatus = status
		},
		SET_COMPANY_TOGGLE_STATUS(state, status) {
			state.companyToggleStatus = status
		},

		// USER
		SET_USER_MODULES(state, data) {
			state.userModules = data
		},
		SET_USER_MODULES_STATUS(state, status) {
			state.userModulesStatus = status
		},
		SET_USER_TOGGLE_STATUS(state, status) {
			state.userToggleStatus = status
		},
	},
	actions: {
		// ─── MODULES CRUD ───────────────────────────────────
		async fetchModulesList({ commit }, params) {
			commit('SET_MODULES_STATUS', 'pending')
			try {
				const response = await modulesList(params)
				commit('SET_MODULES_LIST', response.data)
				commit('SET_MODULES_STATUS', 'init')
				return response.data.data || []
			} catch (error) {
				commit('SET_MODULES_STATUS', 'init')
				throw error
			}
		},
		async fetchModuleByID({ commit }, id) {
			try {
				const response = await moduleByID(id)
				commit('SET_MODULE', response.data.data)
				return response.data.data
			} catch (error) {
				throw error
			}
		},
		async saveNewModule({ commit }, params) {
			commit('SET_MODULES_SAVE_STATUS', 'pending')
			try {
				const response = await moduleCreate(params)
				commit('SET_MODULES_SAVE_STATUS', 'init')
				return response.data
			} catch (error) {
				commit('SET_MODULES_SAVE_STATUS', 'init')
				throw error
			}
		},
		async updateModule({ commit }, params) {
			commit('SET_MODULES_SAVE_STATUS', 'pending')
			try {
				const response = await moduleUpdate(params)
				commit('SET_MODULES_SAVE_STATUS', 'init')
				return response.data
			} catch (error) {
				commit('SET_MODULES_SAVE_STATUS', 'init')
				throw error
			}
		},
		async deleteModuleByID({ commit }, params) {
			commit('SET_MODULES_DELETE_STATUS', 'pending')
			try {
				await moduleDestroy(params)
				commit('SET_MODULES_DELETE_STATUS', 'init')
			} catch (error) {
				commit('SET_MODULES_DELETE_STATUS', 'init')
				throw error
			}
		},

		// ─── PLATFORM OVERVIEW ──────────────────────────────
		async fetchPlatformOverview({ commit }, platformId) {
			commit('SET_PLATFORM_OVERVIEW_STATUS', 'pending')
			try {
				const response = await modulePlatformOverview(platformId)
				commit('SET_PLATFORM_OVERVIEW', response.data.data)
				commit('SET_PLATFORM_OVERVIEW_STATUS', 'init')
			} catch (error) {
				commit('SET_PLATFORM_OVERVIEW_STATUS', 'init')
				throw error
			}
		},

		// ─── COMPANY MODULE ACCESS ──────────────────────────
		async fetchCompanyModules({ commit }, companyId) {
			commit('SET_COMPANY_MODULES_STATUS', 'pending')
			try {
				const response = await companyModuleAccess(companyId)
				commit('SET_COMPANY_MODULES', response.data.data)
				commit('SET_COMPANY_MODULES_STATUS', 'init')
			} catch (error) {
				commit('SET_COMPANY_MODULES_STATUS', 'init')
				throw error
			}
		},
		async toggleCompanyModuleAccess({ commit }, params) {
			commit('SET_COMPANY_TOGGLE_STATUS', 'pending')
			try {
				const response = await toggleCompanyModule(params)
				commit('SET_COMPANY_TOGGLE_STATUS', 'init')
				return response.data
			} catch (error) {
				commit('SET_COMPANY_TOGGLE_STATUS', 'init')
				throw error
			}
		},
		async bulkToggleCompanyModulesAccess({ commit }, params) {
			commit('SET_COMPANY_TOGGLE_STATUS', 'pending')
			try {
				const response = await bulkToggleCompanyModules(params)
				commit('SET_COMPANY_TOGGLE_STATUS', 'init')
				return response.data
			} catch (error) {
				commit('SET_COMPANY_TOGGLE_STATUS', 'init')
				throw error
			}
		},

		// ─── USER MODULE ACCESS ─────────────────────────────
		async fetchUserModules({ commit }, userId) {
			commit('SET_USER_MODULES_STATUS', 'pending')
			try {
				const response = await userModuleAccess(userId)
				const raw = response.data
				const modules = Array.isArray(raw) ? raw : (raw.data || [])
				commit('SET_USER_MODULES', { data: modules })
				commit('SET_USER_MODULES_STATUS', 'init')
			} catch (error) {
				commit('SET_USER_MODULES_STATUS', 'init')
				throw error
			}
		},
		async toggleUserModuleAccess({ commit }, params) {
			commit('SET_USER_TOGGLE_STATUS', 'pending')
			try {
				const response = await toggleUserModule(params)
				commit('SET_USER_TOGGLE_STATUS', 'init')
				return response.data
			} catch (error) {
				commit('SET_USER_TOGGLE_STATUS', 'init')
				throw error
			}
		},
		async removeUserModuleAccessByID({ commit }, id) {
			commit('SET_USER_TOGGLE_STATUS', 'pending')
			try {
				await removeUserModuleAccess(id)
				commit('SET_USER_TOGGLE_STATUS', 'init')
			} catch (error) {
				commit('SET_USER_TOGGLE_STATUS', 'init')
				throw error
			}
		},

		// ─── SEARCH HELPERS ─────────────────────────────────
		async searchCompanies(_, params) {
			const response = await companiesList(params)
			return response.data.data || []
		},
	},
}
